import { useEffect, useState, useCallback } from 'react'
import {
  BarChart3, LineChart, Table2, Download, Users, UserCheck, TrendingUp, TrendingDown,
  DollarSign, Network, Award, Target, Trophy, MapPin, AlertTriangle, Crown, ChevronRight,
  Filter, FileSpreadsheet, FileText, PieChart, Activity, Gift, Sparkles,
} from 'lucide-react'
import * as XLSX from 'xlsx'
import api from '../api/client'
import Loader from '../components/Loader'
import SearchableSelect from '../components/SearchableSelect'

function load(key) { try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null } }
const num = (v) => Number(v || 0).toLocaleString('pt-BR')
const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const COLORS = ['#F4791F', '#2563eb', '#16a34a', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16']

// ── gráfico de barras agrupadas (série mensal) ──
function BarsMensal({ serie }) {
  const max = Math.max(1, ...serie.flatMap((s) => [s.clientes, s.licenciados]))
  return (
    <div className="flex items-end gap-3 h-48 px-2">
      {serie.map((s) => (
        <div key={s.mes} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <div className="flex items-end gap-1 h-40 w-full justify-center">
            <div className="w-3.5 rounded-t" style={{ height: `${(s.clientes / max) * 100}%`, background: COLORS[0] }} title={`Clientes: ${s.clientes}`} />
            <div className="w-3.5 rounded-t" style={{ height: `${(s.licenciados / max) * 100}%`, background: COLORS[1] }} title={`Licenciados: ${s.licenciados}`} />
          </div>
          <span className="text-[10px] text-gray-400">{s.mes.slice(5)}/{s.mes.slice(2, 4)}</span>
        </div>
      ))}
    </div>
  )
}

// ── donut (por produto) ──
function Donut({ data }) {
  const total = data.reduce((s, d) => s + d.qtd, 0) || 1
  let acc = 0
  const R = 60, C = 2 * Math.PI * R
  return (
    <div className="flex items-center gap-4 flex-wrap">
      <svg viewBox="0 0 160 160" className="w-40 h-40">
        <circle cx="80" cy="80" r={R} fill="none" stroke="#f1f5f9" strokeWidth="22" />
        {data.map((d, i) => {
          const frac = d.qtd / total; const dash = frac * C
          const el = <circle key={i} cx="80" cy="80" r={R} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="22"
            strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-acc * C} transform="rotate(-90 80 80)" />
          acc += frac; return el
        })}
        <text x="80" y="85" textAnchor="middle" className="fill-gray-700 font-bold" fontSize="18">{total}</text>
      </svg>
      <div className="space-y-1 text-xs">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /><span className="text-gray-600">{d.nome}</span><b className="text-gray-900">{d.qtd}</b></div>
        ))}
        {data.length === 0 && <p className="text-gray-400">Sem vendas.</p>}
      </div>
    </div>
  )
}

// ── barras horizontais (por estado) ──
function HBars({ data, label }) {
  const max = Math.max(1, ...data.map((d) => d.clientes))
  return (
    <div className="space-y-1.5">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-10 text-gray-500 text-right">{d.estado}</span>
          <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(d.clientes / max) * 100}%`, background: COLORS[i % COLORS.length] }} /></div>
          <span className="w-10 font-semibold text-gray-700">{d.clientes}</span>
        </div>
      ))}
      {data.length === 0 && <p className="text-gray-400 text-sm">Sem dados.</p>}
    </div>
  )
}

function KCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow mb-2`}><Icon size={16} className="text-white" /></div>
      <p className="text-xl font-heading font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {sub != null && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  )
}

// ═══════════ COMPONENTES DE GRÁFICO CONFIGURÁVEIS (nova aba) ═══════════
const CHART_EVO = [['barras', 'Barras'], ['colunas', 'Colunas'], ['linha', 'Linha'], ['area', 'Área']]
const CHART_DIST = [['barras', 'Barras'], ['pizza', 'Pizza'], ['rosca', 'Rosca'], ['linha', 'Linha']]

function ChartPicker({ value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-200">
      {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  )
}

// Gráfico multi-série (evolução): barras/colunas/linha/área
function MultiChart({ serie, series, tipo }) {
  const W = 620, H = 210, pad = 30
  const n = serie.length || 1
  const max = Math.max(1, ...serie.flatMap((s) => series.map((se) => s[se.key] || 0)))
  const x = (i) => pad + (n === 1 ? (W - 2 * pad) / 2 : (i * (W - 2 * pad)) / (n - 1))
  const y = (v) => H - pad - (v / max) * (H - 2 * pad)
  const isBar = tipo === 'barras' || tipo === 'colunas'
  const slot = (W - 2 * pad) / n
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[520px]" style={{ height: 230 }}>
        {[0, 0.25, 0.5, 0.75, 1].map((g, i) => (
          <line key={i} x1={pad} x2={W - pad} y1={pad + g * (H - 2 * pad)} y2={pad + g * (H - 2 * pad)} stroke="#f1f5f9" strokeWidth="1" />
        ))}
        {isBar ? serie.map((s, i) => (
          <g key={i}>
            {series.map((se, j) => {
              const bw = Math.max(3, (slot * 0.6) / series.length)
              const bx = pad + i * slot + slot * 0.2 + j * bw
              const h = ((s[se.key] || 0) / max) * (H - 2 * pad)
              return <rect key={j} x={bx} y={H - pad - h} width={bw - 1} height={h} rx="2" fill={se.color} />
            })}
          </g>
        )) : series.map((se, j) => {
          const pts = serie.map((s, i) => `${x(i)},${y(s[se.key] || 0)}`).join(' ')
          return (
            <g key={j}>
              {tipo === 'area' && <polygon points={`${pad},${H - pad} ${pts} ${W - pad},${H - pad}`} fill={se.color} opacity="0.12" />}
              <polyline points={pts} fill="none" stroke={se.color} strokeWidth="2.5" strokeLinejoin="round" />
              {serie.map((s, i) => <circle key={i} cx={x(i)} cy={y(s[se.key] || 0)} r="3" fill={se.color} />)}
            </g>
          )
        })}
        {serie.map((s, i) => (
          <text key={i} x={pad + i * slot + slot / 2} y={H - 8} textAnchor="middle" fontSize="10" className="fill-gray-400">
            {s.mes.slice(5)}/{s.mes.slice(2, 4)}
          </text>
        ))}
      </svg>
      <div className="flex flex-wrap gap-3 justify-center mt-1">
        {series.map((se) => (
          <span key={se.key} className="flex items-center gap-1 text-[11px] text-gray-500"><span className="w-2.5 h-2.5 rounded" style={{ background: se.color }} />{se.label}</span>
        ))}
      </div>
    </div>
  )
}

// Distribuição categórica: barras/pizza/rosca/linha
function DistChart({ data, tipo }) {
  const total = data.reduce((s, d) => s + (d.qtd || 0), 0) || 1
  if (tipo === 'barras' || tipo === 'linha') {
    const max = Math.max(1, ...data.map((d) => d.qtd || 0))
    if (tipo === 'barras') {
      return (
        <div className="space-y-1.5">
          {data.map((d, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-24 text-gray-500 text-right truncate">{d.nome}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${((d.qtd || 0) / max) * 100}%`, background: d.cor || COLORS[i % COLORS.length] }} /></div>
              <span className="w-12 font-semibold text-gray-700 text-right">{num(d.qtd)}</span>
            </div>
          ))}
        </div>
      )
    }
    // linha
    const W = 560, H = 190, pad = 30, n = data.length || 1
    const x = (i) => pad + (n === 1 ? 0 : (i * (W - 2 * pad)) / (n - 1))
    const y = (v) => H - pad - (v / max) * (H - 2 * pad)
    const pts = data.map((d, i) => `${x(i)},${y(d.qtd || 0)}`).join(' ')
    return (
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[480px]" style={{ height: 200 }}>
          <polyline points={pts} fill="none" stroke={COLORS[0]} strokeWidth="2.5" />
          {data.map((d, i) => <g key={i}><circle cx={x(i)} cy={y(d.qtd || 0)} r="3.5" fill={d.cor || COLORS[0]} /><text x={x(i)} y={H - 8} textAnchor="middle" fontSize="9" className="fill-gray-400">{d.nome.slice(0, 6)}</text></g>)}
        </svg>
      </div>
    )
  }
  // pizza / rosca
  const cx = 80, cy = 80, R = 62
  const polar = (r, ang) => { const a = (ang - 90) * Math.PI / 180; return [cx + r * Math.cos(a), cy + r * Math.sin(a)] }
  let acc = 0
  const arcs = data.map((d, i) => {
    const frac = (d.qtd || 0) / total
    const a0 = acc * 360, a1 = (acc + frac) * 360; acc += frac
    const [x0, y0] = polar(R, a0), [x1, y1] = polar(R, a1)
    const large = a1 - a0 > 180 ? 1 : 0
    const path = tipo === 'pizza'
      ? `M${cx},${cy} L${x0},${y0} A${R},${R} 0 ${large} 1 ${x1},${y1} Z`
      : null
    return { path, frac, color: d.cor || COLORS[i % COLORS.length], a0, a1 }
  })
  const C = 2 * Math.PI * R
  let acc2 = 0
  return (
    <div className="flex items-center gap-4 flex-wrap">
      <svg viewBox="0 0 160 160" className="w-40 h-40">
        {tipo === 'pizza'
          ? arcs.map((a, i) => <path key={i} d={a.path} fill={a.color} stroke="#fff" strokeWidth="1" />)
          : (<><circle cx={cx} cy={cy} r={R} fill="none" stroke="#f1f5f9" strokeWidth="20" />
              {data.map((d, i) => { const frac = (d.qtd || 0) / total; const dash = frac * C
                const el = <circle key={i} cx={cx} cy={cy} r={R} fill="none" stroke={d.cor || COLORS[i % COLORS.length]} strokeWidth="20" strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-acc2 * C} transform={`rotate(-90 ${cx} ${cy})`} />
                acc2 += frac; return el })}
              <text x={cx} y={cy + 5} textAnchor="middle" className="fill-gray-700 font-bold" fontSize="16">{num(total)}</text></>)}
      </svg>
      <div className="space-y-1 text-xs">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: d.cor || COLORS[i % COLORS.length] }} /><span className="text-gray-600">{d.nome}</span><b className="text-gray-900">{num(d.qtd)}</b></div>
        ))}
      </div>
    </div>
  )
}

function Avatar({ foto, nome }) {
  const ini = (nome || '?').split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase()
  return foto
    ? <img src={foto} alt="" className="w-9 h-9 rounded-full object-cover" />
    : <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-xs font-bold">{ini}</div>
}

function Progress({ pct, cor }) {
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: cor || '#F4791F' }} />
    </div>
  )
}

export default function BI() {
  const user = load('user') || {}
  const isAdmin = (user.perfil || '') === 'admin'
  const uid = user.id || user.email || 'x'
  const [view, setView] = useState('carreira')
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(true)
  const [licSel, setLicSel] = useState('')
  const [opcoes, setOpcoes] = useState([])

  // ── nova aba: Qualificações & Evolução da Rede ──
  const [carr, setCarr] = useState(null)
  const [carrLoad, setCarrLoad] = useState(false)
  const [fMeses, setFMeses] = useState(6)
  const [fEstado, setFEstado] = useState('')
  const [fStatus, setFStatus] = useState('')
  const [fRegiao, setFRegiao] = useState('')
  const [fCidade, setFCidade] = useState('')
  const [fStatusQual, setFStatusQual] = useState('')
  const [fServico, setFServico] = useState('')
  const [fParceiro, setFParceiro] = useState('')
  const [fCampanha, setFCampanha] = useState('')
  const [statusView, setStatusView] = useState('ambos') // permanente | mes | ambos
  const [mapaView, setMapaView] = useState('estado')     // estado | regiao
  const [tEvo, setTEvo] = useState(() => localStorage.getItem(`bi_evo_${uid}`) || 'barras')
  const [tDist, setTDist] = useState(() => localStorage.getItem(`bi_dist_${uid}`) || 'rosca')
  const [linhaSel, setLinhaSel] = useState('')
  const [linha, setLinha] = useState(null)
  useEffect(() => { localStorage.setItem(`bi_evo_${uid}`, tEvo) }, [tEvo, uid])
  useEffect(() => { localStorage.setItem(`bi_dist_${uid}`, tDist) }, [tDist, uid])

  useEffect(() => { if (isAdmin) api.get('/licenciados?limit=100').then((r) => setOpcoes(r.data.data || [])).catch(() => {}) }, [isAdmin])
  const carregar = useCallback(() => {
    setLoading(true)
    api.get(`/rede/bi${licSel ? '?licenciado_id=' + licSel : ''}`).then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false))
  }, [licSel])
  useEffect(() => { carregar() }, [carregar])

  const carregarCarreira = useCallback(() => {
    setCarrLoad(true)
    const q = new URLSearchParams()
    q.set('meses', fMeses)
    if (licSel) q.set('licenciado_id', licSel)
    if (fEstado) q.set('estado', fEstado)
    if (fStatus) q.set('status_perm', fStatus)
    if (fRegiao) q.set('regiao', fRegiao)
    if (fCidade) q.set('cidade', fCidade)
    if (fStatusQual) q.set('status_qual', fStatusQual)
    if (fServico) q.set('servico', fServico)
    if (fParceiro) q.set('parceiro_id', fParceiro)
    if (fCampanha) q.set('campanha_id', fCampanha)
    api.get(`/rede/bi-carreira?${q}`).then((r) => setCarr(r.data)).catch(() => setCarr(null)).finally(() => setCarrLoad(false))
  }, [fMeses, fEstado, fStatus, fRegiao, fCidade, fStatusQual, fServico, fParceiro, fCampanha, licSel])
  useEffect(() => { if (view === 'carreira') carregarCarreira() }, [view, carregarCarreira])

  const abrirLinha = (id) => {
    setLinhaSel(id)
    api.get(`/rede/bi-linha/${id}?meses=${fMeses}`).then((r) => setLinha(r.data)).catch(() => setLinha(null))
  }

  // ── exportações ──
  const exportarCSV = () => {
    if (!d) return
    const linhas = [['Mês', 'Novos Clientes', 'Novos Licenciados'], ...d.serie_mensal.map((s) => [s.mes, s.clientes, s.licenciados])]
    const csv = linhas.map((r) => r.join(';')).join('\n')
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    a.download = `bi_crescimento_${new Date().toISOString().slice(0, 10)}.csv`; a.click()
  }
  const exportarExcel = () => {
    if (!carr) return
    const wb = XLSX.utils.book_new()
    const r = carr.resumo
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Indicador', 'Valor'],
      ['Total de Licenciados', r.total_licenciados], ['Total de Clientes', r.total_clientes],
      ['Licenciados Ativos', r.licenciados_ativos], ['Qualificados no Ciclo', r.qualificados_ciclo],
      ['Promoções no período', r.promocoes_periodo], ['Requalificações', r.requalificacoes],
      ['Sem Qualificação', r.sem_qualificacao], ['Presidentes', r.presidentes], ['Acionistas', r.acionistas],
      ['Diretores', r.diretores], ['Executivos', r.executivos], ['Crescimento %', r.crescimento_pct],
    ]), 'Resumo')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Status', 'Permanente', 'Qualificado do Mês'], ...carr.por_status.map((s) => [s.nome, s.permanente, s.mes]),
    ]), 'Por Status')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Nome', 'Status Atual', 'Próximo', 'PV Atual', 'Meta', '%'], ...carr.proximos.map((p) => [p.nome, p.status_atual, p.proximo_status, p.pv_atual, p.meta, p.pct]),
    ]), 'Próximos')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Mês', 'Promoções', 'Qualificações', 'Novos'], ...carr.evolucao.map((e) => [e.mes, e.promocoes, e.qualificacoes, e.novos]),
    ]), 'Evolução')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Mês', 'Licenciado', 'De', 'Para', 'Pontuação', 'Responsável'], ...carr.historico.map((h) => [h.mes, h.licenciado, h.de, h.para, h.pontuacao, h.responsavel]),
    ]), 'Histórico')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Estado', 'Região', 'Total', 'Presidentes', 'Acionistas', 'Diretores', 'Executivos', 'Gestores'],
      ...carr.mapa.map((m) => [m.estado, m.regiao, m.total, m.presidentes, m.acionistas, m.diretores, m.executivos, m.gestores]),
    ]), 'Mapa')
    XLSX.writeFile(wb, `bi_carreira_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }
  const exportarPDF = () => window.print()

  const K = d?.kpis || {}
  const F = d?.financeiro
  const R = carr?.resumo || {}

  const TABS = [
    ['carreira', 'Qualificações & Evolução', Award],
    ['dashboard', 'Dashboard Executivo', BarChart3],
    ['graficos', 'Gráficos', LineChart],
    ['tabela', 'Tabela Analítica', Table2],
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow"><BarChart3 size={20} className="text-white" /></div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-gray-900">Crescimento & BI</h1>
            <p className="text-sm text-gray-500">{isAdmin ? 'Inteligência de negócio de toda a operação.' : 'Acompanhe o crescimento da sua organização.'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && <SearchableSelect value={licSel} onChange={setLicSel} placeholder="Global / rede de..." className="w-52" options={opcoes.map((l) => ({ value: l.id, label: l.nome }))} />}
          {view === 'carreira' ? (
            <>
              <button onClick={exportarExcel} className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-600 px-3 py-2 rounded-xl text-xs font-semibold hover:bg-gray-50"><FileSpreadsheet size={14} /> Excel</button>
              <button onClick={exportarPDF} className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-600 px-3 py-2 rounded-xl text-xs font-semibold hover:bg-gray-50"><FileText size={14} /> PDF</button>
            </>
          ) : (
            <button onClick={exportarCSV} className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-600 px-3 py-2 rounded-xl text-xs font-semibold"><Download size={14} /> CSV</button>
          )}
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setView(k)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${view === k ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}><Icon size={14} /> {l}</button>
        ))}
      </div>

      {/* ═══════════ NOVA ABA: QUALIFICAÇÕES & EVOLUÇÃO DA REDE ═══════════ */}
      {view === 'carreira' && (
        carrLoad ? <div className="flex justify-center py-16"><Loader text="Carregando painel de carreira..." /></div> : !carr ? <p className="text-gray-400 py-10 text-center">Sem dados.</p> : (
          <div className="space-y-5">
            {/* FILTROS GERAIS (#10) */}
            <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1 text-xs font-semibold text-gray-500"><Filter size={13} /> Filtros:</span>
              <select value={fMeses} onChange={(e) => setFMeses(Number(e.target.value))} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600">
                {[3, 6, 12, 24].map((m) => <option key={m} value={m}>Últimos {m} meses</option>)}
              </select>
              <select value={fRegiao} onChange={(e) => setFRegiao(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600">
                <option value="">Todas as regiões</option>
                {(carr.filtros?.regioes || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <select value={fEstado} onChange={(e) => setFEstado(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600">
                <option value="">Todos os estados</option>
                {(carr.filtros?.estados || []).map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
              <select value={fCidade} onChange={(e) => setFCidade(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600 max-w-[140px]">
                <option value="">Todas as cidades</option>
                {(carr.filtros?.cidades || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600">
                <option value="">Status permanente</option>
                {(carr.filtros?.status || []).map((s) => <option key={s.ordem} value={s.ordem}>{s.nome}</option>)}
              </select>
              <select value={fStatusQual} onChange={(e) => setFStatusQual(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600">
                <option value="">Status do mês</option>
                {(carr.filtros?.status || []).map((s) => <option key={s.ordem} value={s.ordem}>{s.nome}</option>)}
              </select>
              <select value={fServico} onChange={(e) => setFServico(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600">
                <option value="">Todos os serviços</option>
                {(carr.filtros?.servicos || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {(carr.filtros?.parceiros || []).length > 0 && (
                <select value={fParceiro} onChange={(e) => setFParceiro(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600 max-w-[140px]">
                  <option value="">Todos os parceiros</option>
                  {(carr.filtros?.parceiros || []).map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              )}
              <select value={fCampanha} onChange={(e) => setFCampanha(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600 max-w-[150px]">
                <option value="">Todas as campanhas</option>
                {(carr.filtros?.campanhas || []).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
              {(fEstado || fStatus || fRegiao || fCidade || fStatusQual || fServico || fParceiro || fCampanha || fMeses !== 6) &&
                <button onClick={() => { setFEstado(''); setFStatus(''); setFRegiao(''); setFCidade(''); setFStatusQual(''); setFServico(''); setFParceiro(''); setFCampanha(''); setFMeses(6) }} className="text-xs text-orange-600 font-semibold">limpar</button>}
              <span className="ml-auto text-[11px] text-gray-400">Mês-ref: {carr.mes_referencia} · escopo: {carr.escopo}</span>
            </div>

            {/* 1) RESUMO EXECUTIVO */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">1 · Resumo Executivo</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KCard icon={Users} label="Total de Licenciados" value={num(R.total_licenciados)} color="from-indigo-400 to-violet-500" />
                <KCard icon={Users} label="Total de Clientes" value={num(R.total_clientes)} color="from-emerald-400 to-teal-500" />
                <KCard icon={UserCheck} label="Licenciados Ativos" value={num(R.licenciados_ativos)} color="from-green-400 to-emerald-500" />
                <KCard icon={Target} label="Qualificados no Ciclo" value={num(R.qualificados_ciclo)} color="from-orange-400 to-orange-600" />
                <KCard icon={TrendingUp} label="Promoções no período" value={num(R.promocoes_periodo)} color="from-blue-400 to-cyan-500" />
                <KCard icon={Award} label="Requalificações" value={num(R.requalificacoes)} color="from-sky-400 to-blue-500" />
                <KCard icon={AlertTriangle} label="Sem Qualificação" value={num(R.sem_qualificacao)} color="from-gray-400 to-gray-500" />
                <KCard icon={Crown} label="Presidentes" value={num(R.presidentes)} color="from-yellow-400 to-amber-600" />
                <KCard icon={Crown} label="Acionistas" value={num(R.acionistas)} color="from-red-400 to-rose-600" />
                <KCard icon={Trophy} label="Diretores" value={num(R.diretores)} color="from-gray-700 to-gray-900" />
                <KCard icon={Trophy} label="Executivos" value={num(R.executivos)} color="from-orange-400 to-orange-600" />
                <KCard icon={TrendingUp} label="Crescimento da Rede" value={`${num(R.crescimento_pct)}%`} color="from-emerald-500 to-green-600" sub={`${num(R.novos_periodo)} novos no período`} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* 2) QUANTIDADE POR STATUS (permanente × mês) */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-800">2 · Licenciados por Status</p>
                  <div className="flex gap-1">
                    {[['ambos', 'Ambos'], ['permanente', 'Permanente'], ['mes', 'Do mês']].map(([k, l]) => (
                      <button key={k} onClick={() => setStatusView(k)} className={`text-[11px] px-2 py-0.5 rounded ${statusView === k ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-gray-400 text-xs uppercase text-left"><tr><th className="py-1">Status</th>{(statusView !== 'mes') && <th className="py-1 text-right">Permanente</th>}{(statusView !== 'permanente') && <th className="py-1 text-right">Qualif. do Mês</th>}</tr></thead>
                  <tbody>
                    {carr.por_status.map((s) => (
                      <tr key={s.ordem} className="border-t border-gray-50">
                        <td className="py-1.5"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.cor }} />{s.nome}</span></td>
                        {(statusView !== 'mes') && <td className="py-1.5 text-right font-semibold text-gray-800">{num(s.permanente)}</td>}
                        {(statusView !== 'permanente') && <td className="py-1.5 text-right text-gray-600">{num(s.mes)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 6) DISTRIBUIÇÃO DOS STATUS (configurável) */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><PieChart size={15} /> 6 · Distribuição dos Status</p>
                  <ChartPicker value={tDist} onChange={setTDist} options={CHART_DIST} />
                </div>
                <DistChart data={carr.distribuicao} tipo={tDist} />
              </div>
            </div>

            {/* 5) EVOLUÇÃO DAS QUALIFICAÇÕES (configurável) */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Activity size={15} /> 5 · Evolução das Qualificações</p>
                <ChartPicker value={tEvo} onChange={setTEvo} options={CHART_EVO} />
              </div>
              <MultiChart serie={carr.evolucao} tipo={tEvo} series={[
                { key: 'promocoes', label: 'Promoções', color: COLORS[0] },
                { key: 'qualificacoes', label: 'Qualificações', color: COLORS[1] },
                { key: 'novos', label: 'Novos Licenciados', color: COLORS[2] },
              ]} />
            </div>

            {/* 3) PRÓXIMOS DA QUALIFICAÇÃO */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5"><Target size={15} /> 3 · Licenciados Próximos da Qualificação</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {carr.proximos.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-2 rounded-xl border border-gray-100">
                    <Avatar foto={p.foto} nome={p.nome} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-800 truncate">{p.nome}</p>
                        <span className="text-xs font-bold" style={{ color: p.cor }}>{p.pct}%</span>
                      </div>
                      <p className="text-[11px] text-gray-500 mb-1">{p.status_atual} <ChevronRight size={10} className="inline" /> <b style={{ color: p.cor }}>{p.proximo_status}</b> · faltam {num(p.restante)} PV{p.tempo_estimado ? ` · ${p.tempo_estimado}` : ''}</p>
                      <Progress pct={p.pct} cor={p.cor} />
                    </div>
                    <button onClick={() => abrirLinha(p.id)} className="text-[11px] text-orange-600 font-semibold whitespace-nowrap">Detalhes</button>
                  </div>
                ))}
                {carr.proximos.length === 0 && <p className="text-gray-400 text-sm col-span-2 text-center py-4">Ninguém próximo de qualificar no filtro atual.</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* 4) PRÓXIMAS PROMOÇÕES E CAMPANHAS */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5"><Gift size={15} /> 4 · Promoções & Campanhas</p>
                <div className="space-y-2">
                  {carr.campanhas.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 p-2 rounded-xl bg-gray-50">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center"><Trophy size={14} className="text-white" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{c.nome}</p>
                        <p className="text-[11px] text-gray-500">{c.premio || c.categoria} · <b className="text-orange-600">{num(c.proximos)}</b> elegíveis{c.data_fim ? ` · até ${c.data_fim}` : ''}</p>
                      </div>
                    </div>
                  ))}
                  {carr.campanhas.length === 0 && <p className="text-gray-400 text-sm text-center py-4">Nenhuma campanha ativa.</p>}
                </div>
              </div>

              {/* 12) BI PREDITIVO */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5"><Sparkles size={15} /> 12 · BI Preditivo <span className="text-[11px] font-normal text-gray-400">(potencial de promoção)</span></p>
                <div className="grid grid-cols-2 gap-3">
                  <KCard icon={Trophy} label="Podem virar Executivo" value={num(carr.preditivo.executivo)} color="from-orange-400 to-orange-600" />
                  <KCard icon={Trophy} label="Podem virar Diretor" value={num(carr.preditivo.diretor)} color="from-gray-700 to-gray-900" />
                  <KCard icon={Crown} label="Podem virar Acionista" value={num(carr.preditivo.acionista)} color="from-red-400 to-rose-600" />
                  <KCard icon={Crown} label="Podem virar Presidente" value={num(carr.preditivo.presidente)} color="from-yellow-400 to-amber-600" />
                </div>
              </div>
            </div>

            {/* 13) ALERTAS */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5"><AlertTriangle size={15} /> 13 · Alertas Inteligentes</p>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {carr.alertas.map((a, i) => {
                  const c = a.nivel === 'danger' ? 'bg-red-50 text-red-700 border-red-100' : a.nivel === 'warn' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-blue-50 text-blue-700 border-blue-100'
                  return <div key={i} className={`rounded-xl border p-2.5 ${c}`}><p className="text-lg font-bold">{num(a.count)}</p><p className="text-[11px] leading-tight">{a.texto}</p></div>
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* 7) HISTÓRICO DE PROMOÇÕES */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5"><TrendingUp size={15} /> 7 · Histórico de Promoções</p>
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {carr.historico.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs border-l-2 border-orange-300 pl-2 py-0.5">
                      <span className="text-gray-400 w-14">{h.mes}</span>
                      <span className="font-semibold text-gray-800 flex-1 truncate">{h.licenciado}</span>
                      <span className="text-gray-500">{h.de} <ChevronRight size={10} className="inline" /> <b className="text-orange-600">{h.para}</b></span>
                    </div>
                  ))}
                  {carr.historico.length === 0 && <p className="text-gray-400 text-sm text-center py-4">Sem promoções no período (rode o fechamento mensal).</p>}
                </div>
              </div>

              {/* 8) EVOLUÇÃO POR LINHA */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5"><Network size={15} /> 8 · Evolução por Linha</p>
                {isAdmin && (
                  <SearchableSelect value={linhaSel} onChange={(v) => abrirLinha(v)} placeholder="Selecione um licenciado..." className="w-full mb-3"
                    options={opcoes.map((l) => ({ value: l.id, label: l.nome }))} />
                )}
                {!isAdmin && !linha && <button onClick={() => abrirLinha('me')} className="text-xs text-orange-600 font-semibold mb-2">Ver minha linha</button>}
                {linha ? (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-800">{linha.licenciado?.nome} <span className="text-xs text-gray-400">({linha.licenciado?.status})</span></p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-gray-50 rounded-lg p-2"><p className="text-lg font-bold text-gray-800">{num(linha.equipe_total)}</p><p className="text-[10px] text-gray-500">Equipe</p></div>
                      <div className="bg-gray-50 rounded-lg p-2"><p className="text-lg font-bold text-green-600">{num(linha.equipe_ativos)}</p><p className="text-[10px] text-gray-500">Ativos</p></div>
                      <div className="bg-gray-50 rounded-lg p-2"><p className="text-lg font-bold text-orange-600">{num(linha.producao_acumulada)}</p><p className="text-[10px] text-gray-500">Produção (PV)</p></div>
                      <div className="bg-gray-50 rounded-lg p-2"><p className="text-lg font-bold text-gray-800">{num(linha.qualificados)}</p><p className="text-[10px] text-gray-500">Qualificados</p></div>
                      <div className="bg-gray-50 rounded-lg p-2"><p className="text-lg font-bold text-blue-600">{num(linha.promocoes_linha)}</p><p className="text-[10px] text-gray-500">Promoções</p></div>
                      <div className="bg-gray-50 rounded-lg p-2"><p className="text-lg font-bold text-gray-800">{num(linha.proximos?.length)}</p><p className="text-[10px] text-gray-500">Prox. qualif.</p></div>
                    </div>
                    {linha.proximos?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-[11px] font-semibold text-gray-500 uppercase">Próximos qualificados da equipe</p>
                        {linha.proximos.slice(0, 5).map((p) => (
                          <div key={p.id} className="flex items-center gap-2 text-xs"><Avatar foto={p.foto} nome={p.nome} /><span className="flex-1 truncate text-gray-700">{p.nome}</span><span className="text-gray-400">{p.proximo_status}</span><b className="text-orange-600">{p.pct}%</b></div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : <p className="text-gray-400 text-sm text-center py-6">Selecione um licenciado para ver a evolução da linha.</p>}
              </div>
            </div>

            {/* 9) MAPA DE QUALIFICAÇÕES (por estado/região) */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><MapPin size={15} /> 9 · Mapa de Qualificações</p>
                <div className="flex gap-1">
                  {[['estado', 'Por Estado'], ['regiao', 'Por Região']].map(([k, l]) => (
                    <button key={k} onClick={() => setMapaView(k)} className={`text-[11px] px-2 py-0.5 rounded ${mapaView === k ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="text-gray-400 text-xs uppercase text-left"><tr>
                    <th className="py-1">{mapaView === 'estado' ? 'Estado' : 'Região'}</th><th className="py-1 text-right">Total</th>
                    <th className="py-1 text-right">Pres.</th><th className="py-1 text-right">Acion.</th><th className="py-1 text-right">Diret.</th><th className="py-1 text-right">Exec.</th><th className="py-1 text-right">Gest.</th>
                  </tr></thead>
                  <tbody>
                    {(() => {
                      let rows = carr.mapa
                      if (mapaView === 'regiao') {
                        const agg = {}
                        carr.mapa.forEach((m) => {
                          const r = agg[m.regiao] || (agg[m.regiao] = { estado: m.regiao, total: 0, presidentes: 0, acionistas: 0, diretores: 0, executivos: 0, gestores: 0 })
                          r.total += m.total; r.presidentes += m.presidentes; r.acionistas += m.acionistas; r.diretores += m.diretores; r.executivos += m.executivos; r.gestores += m.gestores
                        })
                        rows = Object.values(agg).sort((a, b) => b.total - a.total)
                      }
                      const max = Math.max(1, ...rows.map((r) => r.total))
                      return rows.map((m, i) => (
                        <tr key={i} className="border-t border-gray-50">
                          <td className="py-1.5"><div className="flex items-center gap-2"><span className="w-8 font-semibold text-gray-700">{m.estado}</span><div className="w-24 bg-gray-100 rounded-full h-2 hidden sm:block"><div className="h-full rounded-full bg-orange-400" style={{ width: `${(m.total / max) * 100}%` }} /></div></div></td>
                          <td className="py-1.5 text-right font-semibold text-gray-800">{num(m.total)}</td>
                          <td className="py-1.5 text-right text-gray-500">{m.presidentes}</td><td className="py-1.5 text-right text-gray-500">{m.acionistas}</td>
                          <td className="py-1.5 text-right text-gray-500">{m.diretores}</td><td className="py-1.5 text-right text-gray-500">{m.executivos}</td><td className="py-1.5 text-right text-gray-500">{m.gestores}</td>
                        </tr>
                      ))
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      )}

      {loading ? <div className="flex justify-center py-16"><Loader text="Carregando BI..." /></div> : !d ? (view !== 'carreira' && <p className="text-gray-400 py-10 text-center">Sem dados.</p>) : (
        <>
          {view === 'dashboard' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KCard icon={Users} label="Clientes (ativos/total)" value={`${num(K.clientes_ativos)}/${num(K.clientes_total)}`} color="from-emerald-400 to-teal-500" />
                <KCard icon={UserCheck} label="Licenciados (ativos/total)" value={`${num(K.licenciados_ativos)}/${num(K.licenciados_total)}`} color="from-indigo-400 to-violet-500" />
                <KCard icon={TrendingUp} label="Produção Pessoal (PV)" value={num(K.producao_pessoal)} color="from-amber-400 to-orange-500" />
                <KCard icon={Network} label="Produção da Equipe (PV)" value={num(K.producao_equipe)} color="from-blue-400 to-cyan-500" />
              </div>
              {F && (
                <>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 pt-2">Financeiro (exclusivo administração)</p>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KCard icon={DollarSign} label="Faturamento total" value={money(F.faturamento_total)} color="from-emerald-500 to-teal-600" />
                    <KCard icon={DollarSign} label="Receita recorrente" value={money(F.receita_recorrente)} color="from-blue-500 to-indigo-600" />
                    <KCard icon={DollarSign} label="Comissões pagas" value={money(F.comissoes_pagas)} color="from-rose-400 to-red-500" />
                    <KCard icon={DollarSign} label="Comissões provisionadas" value={money(F.comissoes_provisionadas)} color="from-amber-400 to-orange-500" />
                    <KCard icon={TrendingUp} label="Ticket médio" value={money(F.ticket_medio)} color="from-violet-400 to-purple-500" />
                    <KCard icon={UserCheck} label="Licenças vencidas" value={num(F.licencas_vencidas)} color="from-gray-400 to-gray-500" />
                    <KCard icon={UserCheck} label="Licenças a vencer (30d)" value={num(F.licencas_proximas)} color="from-amber-400 to-yellow-500" />
                  </div>
                </>
              )}
            </div>
          )}

          {view === 'graficos' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <p className="text-sm font-semibold text-gray-800 mb-3">Crescimento mensal <span className="text-[11px] text-gray-400">(<span style={{ color: COLORS[0] }}>■</span> clientes · <span style={{ color: COLORS[1] }}>■</span> licenciados)</span></p>
                <BarsMensal serie={d.serie_mensal} />
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <p className="text-sm font-semibold text-gray-800 mb-3">Vendas por produto/serviço</p>
                <Donut data={d.por_produto} />
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 lg:col-span-2">
                <p className="text-sm font-semibold text-gray-800 mb-3">Clientes por estado (Top 10)</p>
                <HBars data={d.por_estado} />
              </div>
            </div>
          )}

          {view === 'tabela' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase"><tr><th className="px-4 py-2">Mês</th><th className="px-4 py-2 text-right">Novos Clientes</th><th className="px-4 py-2 text-right">Novos Licenciados</th></tr></thead>
                <tbody>
                  {d.serie_mensal.map((s) => (
                    <tr key={s.mes} className="border-t border-gray-100"><td className="px-4 py-2">{s.mes}</td><td className="px-4 py-2 text-right">{num(s.clientes)}</td><td className="px-4 py-2 text-right">{num(s.licenciados)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
